// What's new: user facing changelog, filtered by role and permissions.
// Add the entries of each release here when its version is bumped
// (npm run version:bump). Only what is useful for the users: internal fixes
// go to docs/CHANGELOG.md. Newest first.
import {
  onCheckReadCaseLogsPermissions,
  onCheckReviewCaseLogsPermissions,
  onCheckSubmitCaseLogsPermissions,
} from "../case-logs/CaseLogsPermissions";
import { onCheckRotationsPermissions } from "../rotations/RotationsPermissions";
import {
  onCheckCreateEvaluationSchedulersPermissions,
  onCheckReadEvaluationSchedulersPermissions,
} from "../evaluations-schedulers/EvaluationSchedulersPermissions";
// Types
import type { WhatsNewEntry } from "./_WhatsNew.types";

const caseLogsLink = {
  pathname: "/case-logs",
  label: "Go to Case Logs",
  namePage: "Case Logs List",
};

const whatsNewEntries: Array<WhatsNewEntry> = [
  // 2.104.0: Case Logs redesign. One entry per kind of user, so each one reads
  // only what changed for them: who submits, who reviews, who only reads
  {
    id: "case-logs-redesigned-student-form",
    version: "2.104.0",
    releasedAt: "2026-11-05T00:00:00.000Z",
    title: "A redesigned Case Log form",
    description:
      "Creating and editing a Case Log is easier. Each section shows whether it is complete, the fields tell you what is missing, and before submitting a summary links every issue to its field. Add several teeth and procedures in one step, upload or take the images right from each image card, and see which entries clash when a Case Log looks duplicated. Your Case Logs and their PDF have a new look too.",
    module: "CaseLogs",
    link: caseLogsLink,
    audience: {
      can: (permissions) => onCheckSubmitCaseLogsPermissions(permissions),
    },
  },
  {
    id: "case-logs-redesigned-review",
    version: "2.104.0",
    releasedAt: "2026-11-05T00:00:00.000Z",
    title: "A redesigned Case Log review",
    description:
      "Reviewing a Case Log is clearer. Move between its sections from the navigation, see each procedure evaluation next to its clinical entry, and approve or reject it from its own panel. Rejecting asks for a note, and the approval note starts empty, so an old rejection note is never sent again. After your decision you stay on the Case Log to see the result. The PDF of approved Case Logs has a new design too.",
    module: "CaseLogs",
    link: caseLogsLink,
    audience: {
      can: (permissions) => onCheckReviewCaseLogsPermissions(permissions),
    },
  },
  {
    id: "case-logs-redesigned-view-pdf",
    version: "2.104.0",
    releasedAt: "2026-11-05T00:00:00.000Z",
    title: "A redesigned Case Log view and PDF",
    description:
      "Case Logs are easier to read: move between their sections from the navigation, see the images in a gallery and every note in one place. The PDF of approved Case Logs has a new, clearer design.",
    module: "CaseLogs",
    link: caseLogsLink,
    audience: {
      // Only readers: who submits or reviews gets its own entry above
      can: (permissions) =>
        onCheckReadCaseLogsPermissions(permissions) &&
        !onCheckSubmitCaseLogsPermissions(permissions) &&
        !onCheckReviewCaseLogsPermissions(permissions),
    },
  },
  {
    id: "rotations-trigger-pre-rotation-evaluations",
    version: "2.103.0",
    releasedAt: "2026-10-28T00:00:00.000Z",
    title: "Trigger evaluations before a rotation ends",
    description:
      "You can now trigger the evaluations of upcoming and in progress rotations, not only of finished ones. Select the rotations in the list and click Trigger Evaluations: upcoming and in progress rotations use the evaluation schedulers of the start of the rotation, such as pre-rotation evaluations, and finished rotations the ones of the end of the rotation.",
    module: "Rotations",
    link: {
      pathname: "/rotations",
      label: "Go to Rotations",
      namePage: "Rotations",
    },
    audience: {
      can: (permissions) =>
        onCheckRotationsPermissions(permissions) &&
        onCheckReadEvaluationSchedulersPermissions(permissions) &&
        onCheckCreateEvaluationSchedulersPermissions(permissions),
    },
  },
];

export default whatsNewEntries;
